<script lang="ts">
  /**
   * GameScreen — main game screen composing Typewriter, ChoiceGrid,
   * HotkeyBar, and Overlays. Wired to Tauri engine and LLM agents
   * for narrative generation.
   *
   * Uses "buffer then type" approach: LLM generates full response,
   * then Typewriter displays it. Skip/fast-forward work perfectly.
   */
  import { gameStore, overlayStore, settingsStore } from "$lib/stores/game.svelte";
  import type { OverlayKind } from "$lib/stores/game.svelte";
  import {
    processAction,
    getWorldState,
    getCharacter,
    saveNarrative,
    recordBeat,
    addStoryArc,
    getNarrativeMemory,
    createPact,
  } from "$lib/api/invoke";
  import type { TurnResult } from "$lib/api/invoke";
  import {
    arcGenerationPrompt,
    narrativeWithBeatPrompt,
    parseCombinedResponse,
    beatSummaryPrompt,
    memoryCompressionPrompt,
  } from "$lib/api/prompts/narrative";
  import { parseLlmJson as parseBeatJson } from "$lib/api/jsonRepair";
  import { NarrativeAgent } from "$lib/api/agents";
  import type { NarrativeContext as PromptNarrativeContext } from "$lib/api/prompts/narrative";
  import { openingScenePrompt } from "$lib/api/prompts/openingScene";
  import { optionGenerationPrompt } from "$lib/api/prompts/options";
  import { parseLlmJson } from "$lib/api/jsonRepair";
  import { DAGExecutor } from "$lib/api/dag";
  import { openingScenePipeline, turnPipeline } from "$lib/api/pipelines";
  import { PromptHealer } from "$lib/api/promptHealer";
  import Typewriter from "./Typewriter.svelte";
  import ChoiceGrid from "./ChoiceGrid.svelte";
  import HotkeyBar from "./HotkeyBar.svelte";
  import OverlayContainer from "./overlays/OverlayContainer.svelte";
  import AsciiAnimation from "./AsciiAnimation.svelte";
  import MinigameOverlay from "./MinigameOverlay.svelte";
  import type { MinigameResult } from "$lib/minigames/manager";

  export interface ResumeData {
    characterName: string;
    characterBackground: string;
    turnNumber: number;
    currentLocation: string;
    recentActions: string[];
    lastNarrative: string;
  }

  interface Props {
    onQuit: () => void;
    isOpeningScene?: boolean;
    worldSetting?: string;
    resumeData?: ResumeData;
  }

  let { onQuit, isOpeningScene = false, worldSetting = "", resumeData }: Props = $props();

  /** Processing indicator flavor texts. */
  const PROCESSING_TEXTS = [
    "The depths stir...",
    "Fate deliberates...",
    "The narrator ponders...",
    "Shadows shift...",
    "Something stirs in the dark...",
  ];

  let narrativeText = $state("");
  let choices = $state<{ label: string; text: string; category: string }[]>([]);
  let typewriterDone = $state(false);
  let freeformInput = $state("");
  let processing = $state(false);
  let processingText = $state("");
  let turnNumber = $state(0);
  let statusLine = $state("");
  let lastSkillSummary = $state("");
  let recentActions = $state<string[]>([]);
  let showMinigame = $state(false);
  let minigameBonus = $state(0);
  let minigameRef = $state<MinigameOverlay | undefined>(undefined);

  /** Agents — initialized lazily. Router handles provider selection per-call. */
  let narrativeAgent: NarrativeAgent | null = null;

  /** Self-healing prompt adjuster — loaded from localStorage. */
  let healer: PromptHealer = PromptHealer.load();

  /** Pact-triggering consequence patterns. */
  const PACT_PATTERNS = ["owes_favor:", "promised:", "bound_to:", "debt:"] as const;

  let characterName = $state("Adventurer");
  let characterBackground = $state("");
  let currentLocation = $state("Unknown location");
  let characterAttributes = $state<{ st: number; dx: number; iq: number; ht: number } | undefined>(
    undefined,
  );
  let characterBestSkills = $state<string[]>([]);

  const isGuided = $derived(settingsStore.inputMode === "guided");

  /** Derive overlay kind as an explicit Svelte signal so the {#if} in
   *  OverlayContainer subscribes to a proper reactive primitive rather
   *  than a plain-object getter, which can lose its subscription
   *  across branch transitions. */
  const activeOverlay = $derived(overlayStore.active);

  /** Narrative background opacity based on animation level — canvas is beneath this div. */
  const narrativeBg = $derived(
    settingsStore.animationLevel === "prominent"
      ? "rgba(0,0,0,0.45)"
      : settingsStore.animationLevel === "subtle"
        ? "rgba(10,10,10,0.82)"
        : "rgba(10,10,10,0.95)",
  );

  /** Hotkey definitions for the bar. */
  const hotkeyDefs = $derived([
    { key: "M", label: "Map", active: typewriterDone && !processing },
    { key: "T", label: "Timeline", active: typewriterDone && !processing },
    { key: "R", label: "Relationships", active: typewriterDone && !processing },
    { key: "J", label: "Journal", active: typewriterDone && !processing },
    { key: "?", label: "Settings", active: true },
    { key: "Q", label: "Quit", active: true },
  ]);

  /** Map hotkey characters to overlay kinds. */
  const OVERLAY_KEYS: Record<string, OverlayKind> = {
    m: "map",
    t: "timeline",
    r: "relationships",
    j: "journal",
    "?": "settings",
    "/": "settings",
  };

  // JSON extraction/repair handled by parseLlmJson from jsonRepair.ts

  function randomProcessingText(): string {
    return PROCESSING_TEXTS[Math.floor(Math.random() * PROCESSING_TEXTS.length)];
  }

  /** Initialize LLM agents. Router handles provider selection per-call. */
  function ensureAgents(): boolean {
    if (narrativeAgent) return true;
    narrativeAgent = new NarrativeAgent({ provider: "gemini", apiKey: "" });
    return true;
  }

  /** Load world state from Rust. */
  async function loadWorldState(): Promise<void> {
    try {
      const ws = await getWorldState();
      characterName = ws.character_name;
      turnNumber = ws.turn;
      statusLine = `Turn ${ws.turn} | ${ws.time} | ${ws.rules_mode}`;

      const charJson = await getCharacter();
      const char = JSON.parse(charJson);
      if (char.background) characterBackground = char.background;
      if (char.st && char.dx && char.iq && char.ht) {
        characterAttributes = { st: char.st, dx: char.dx, iq: char.iq, ht: char.ht };
      }
      if (char.skills && typeof char.skills === "object") {
        const skills = char.skills as Record<string, number>;
        characterBestSkills = Object.entries(skills)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name]) => name);
      }
    } catch {
      // non-fatal
    }
  }

  /** Generate 4 guided options via LLM, with one retry on failure. */
  async function generateOptions(retries = 1): Promise<void> {
    if (!narrativeAgent) return;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const prompt =
          optionGenerationPrompt(
            worldSetting,
            characterName,
            currentLocation,
            narrativeText,
            recentActions,
            turnNumber,
            undefined, // arcs not available for standalone option generation
            characterBestSkills.length > 0 ? characterBestSkills : undefined,
            characterAttributes,
          ) + healer.healingSuffix("option-generation");
        const systemContext =
          "You generate action options for a dark roguelike RPG. Return ONLY a valid JSON array, no other text.";
        const raw = await narrativeAgent.generateWithRouter(
          prompt,
          "option-generation",
          systemContext,
        );
        const parsed = parseLlmJson(raw);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          choices = parsed.slice(0, 4);
          healer.recordSuccess("option-generation");
          return;
        }
        healer.recordFailure("option-generation", "structure_mismatch");
        console.warn("[Options] LLM returned invalid format, retrying...", parsed);
      } catch (err) {
        healer.recordFailure("option-generation", "json_parse");
        console.warn(`[Options] Attempt ${attempt + 1} failed:`, err);
      }
    }
    // Minimal fallback only after all retries exhausted
    choices = [
      {
        label: "Look Around",
        text: "Survey your immediate surroundings for danger or opportunity.",
        category: "cautious",
      },
      { label: "Press On", text: "Push forward into the unknown.", category: "aggressive" },
      {
        label: "Improvise",
        text: "Use whatever is at hand in a creative way.",
        category: "creative",
      },
      { label: "Wildcard", text: "Do something nobody would expect.", category: "wildcard" },
    ];
  }

  /** Handle minigame dismissal — captures the score bonus. */
  function handleMinigameDismiss(result: MinigameResult): void {
    minigameBonus = result.bonus;
    showMinigame = false;
  }

  /** Handle opening scene generation via DAG pipeline. */
  async function handleOpeningScene(): Promise<void> {
    processing = true;
    processingText = "You drift between worlds...";
    showMinigame = true;

    await loadWorldState();
    ensureAgents();
    if (!narrativeAgent) {
      narrativeText = "Error: Failed to initialize agent. Return to menu.";
      processing = false;
      return;
    }

    const agent = narrativeAgent;
    const executor = new DAGExecutor();
    const pipeline = openingScenePipeline({
      fetchWorldState: async () =>
        JSON.stringify({ worldSetting, characterName, characterBackground }),
      generateOpening: async (ctx) => {
        const state = JSON.parse(ctx);
        const prompt =
          openingScenePrompt(
            state.worldSetting,
            state.characterName,
            state.characterBackground,
            "dark-fantasy",
            characterBestSkills.length > 0 ? characterBestSkills : undefined,
            characterAttributes,
          ) + healer.healingSuffix("opening-scene");
        return await agent.generateWithRouter(prompt, "opening-scene");
      },
      generateOptions: async (narrativeOutput, ctx) => {
        const state = JSON.parse(ctx);
        const prompt =
          optionGenerationPrompt(
            worldSetting,
            state.characterName,
            currentLocation,
            narrativeOutput,
            recentActions,
            turnNumber,
            undefined,
            characterBestSkills.length > 0 ? characterBestSkills : undefined,
            characterAttributes,
          ) + healer.healingSuffix("option-generation");
        const systemContext =
          "You generate action options for a dark roguelike RPG. Return ONLY a valid JSON array, no other text.";
        return await agent.generateWithRouter(prompt, "option-generation", systemContext);
      },
    });

    const result = await executor.run(pipeline);

    // Extract opening narrative
    if (result.outputs["opening"]) {
      narrativeText = result.outputs["opening"];
      healer.recordSuccess("opening-scene");
    } else {
      const err = result.errors["opening"];
      narrativeText = `The world shimmers into existence around you.\n\nYou stand in darkness. The air is cold and damp. Something drips in the distance.\n\n[LLM generation failed: ${String(err?.message ?? "unknown error")}]`;
      healer.recordFailure("opening-scene", "empty_response");
    }

    // Extract options
    if (result.outputs["options"]) {
      try {
        const parsed = parseLlmJson(result.outputs["options"]);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          choices = parsed.slice(0, 4);
          healer.recordSuccess("option-generation");
        } else {
          healer.recordFailure("option-generation", "structure_mismatch");
        }
      } catch {
        console.warn("[Options] Failed to parse opening scene options");
        healer.recordFailure("option-generation", "json_parse");
      }
    }

    // Save healer state
    healer.save();

    // Dismiss minigame overlay now that content is ready
    if (showMinigame && minigameRef) {
      minigameRef.dismiss();
    }
    showMinigame = false;

    // Persist narrative for resume + generate initial arcs (both best-effort)
    saveNarrative(narrativeText).catch(() => {});
    generateInitialArcs(agent).catch((err) =>
      console.warn("[ArcGeneration] Failed (non-fatal):", err),
    );

    processing = false;

    if (gameStore.state === "opening-scene") {
      gameStore.transition("input-guided");
    }
  }

  /** Generate initial story arcs for a fresh world via LLM. Best-effort. */
  async function generateInitialArcs(agent: NarrativeAgent): Promise<void> {
    const prompt = arcGenerationPrompt(worldSetting, "dark-fantasy", "bleak");
    const systemCtx = "You generate story arcs for a roguelike RPG world. Return ONLY valid JSON.";
    const raw = await agent.generateWithRouter(prompt, "background-generation", systemCtx);
    const parsed = parseLlmJson(raw);
    if (!Array.isArray(parsed)) return;

    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      const arc = item as Record<string, unknown>;
      await addStoryArc({
        id: (arc.id as string) ?? "unknown_arc",
        title: (arc.title as string) ?? "Unnamed Arc",
        description: (arc.description as string) ?? "",
        stakes: (arc.stakes as string) ?? "",
        initial_visibility: (arc.initial_visibility as string) ?? "unknown",
        initial_tension: (arc.initial_tension as number) ?? 0.1,
        decay_behavior: (arc.decay_behavior as string) ?? "escalate",
        related_locations: (arc.related_locations as string[]) ?? [],
        advancement_triggers: (arc.advancement_triggers as string[]) ?? [],
      });
    }
  }

  /** Submit an action to the engine and generate narrative via DAG pipeline. */
  async function submitAction(actionText: string, skill?: string): Promise<void> {
    if (processing) return;
    processing = true;
    processingText = randomProcessingText();
    typewriterDone = false;
    lastSkillSummary = "";
    showMinigame = true;
    minigameBonus = 0;

    // Track recent actions (last 5)
    recentActions = [...recentActions.slice(-4), actionText];

    ensureAgents();
    const agent = narrativeAgent;

    try {
      const executor = new DAGExecutor();
      const pipeline = turnPipeline({
        action: actionText,
        processAction: async (action) => {
          const result: TurnResult = await processAction(action, skill);
          turnNumber = result.turn;

          // Build skill summary (includes minigame bonus if earned)
          if (result.skill_checks.length > 0) {
            const bonusTag = minigameBonus > 0 ? ` [Minigame +${minigameBonus}]` : "";
            lastSkillSummary =
              result.skill_checks
                .map((sc) => {
                  const outcome = sc.success ? "Success" : "Failure";
                  const crit = sc.critical ? " (Critical!)" : "";
                  return `${sc.skill_name}: rolled ${sc.roll} vs ${sc.effective_level} — ${outcome}${crit}`;
                })
                .join(" | ") + bonusTag;
          }

          // Update status — refresh world state for current time
          try {
            const ws = await getWorldState();
            statusLine = `Turn ${result.turn} | ${ws.time} | ${ws.rules_mode}`;
          } catch {
            statusLine = `Turn ${result.turn} | ${result.time_elapsed}`;
          }
          if (result.events.length > 0) {
            statusLine += ` | ${result.events.join(", ")}`;
          }

          return JSON.stringify(result);
        },
        generateNarrative: async (ctx) => {
          if (!agent) return buildFallbackNarrative(JSON.parse(ctx));
          const result: TurnResult = JSON.parse(ctx);
          const nctx = result.narrative_context;
          const promptCtx: PromptNarrativeContext = {
            action: nctx.action,
            rollResults: nctx.skill_results.join("; ") || "No rolls required",
            characterName: nctx.character_name,
            location: nctx.location,
            recentHistory: recentActions,
            activeEffects: nctx.social_notes,
            narrativeDirective: nctx.narrative_directive,
            activeArcs: nctx.active_arcs,
            immediateMemory: nctx.immediate_memory,
            recentMemory: nctx.recent_memory,
            deepMemory: nctx.deep_memory,
          };
          currentLocation = nctx.location;
          // Use combined narrative+beat prompt to eliminate separate beat extraction call
          const prompt =
            narrativeWithBeatPrompt(promptCtx) + healer.healingSuffix("narrative-turn");
          return await agent.generateWithRouter(prompt, "narrative-turn");
        },
        generateOptions: async (narrativeOutput, ctx) => {
          if (!agent) return "[]";
          const result: TurnResult = JSON.parse(ctx);
          const nctx = result.narrative_context;
          const prompt =
            optionGenerationPrompt(
              worldSetting,
              nctx.character_name,
              nctx.location,
              narrativeOutput,
              recentActions,
              turnNumber,
              nctx.active_arcs,
              characterBestSkills.length > 0 ? characterBestSkills : undefined,
              characterAttributes,
            ) + healer.healingSuffix("option-generation");
          const systemContext =
            "You generate action options for a dark roguelike RPG. Return ONLY a valid JSON array, no other text.";
          return await agent.generateWithRouter(prompt, "option-generation", systemContext);
        },
      });

      const dagResult = await executor.run(pipeline);

      // Extract narrative from combined narrative+beat response
      if (dagResult.outputs["narrative"]) {
        const combined = parseCombinedResponse(dagResult.outputs["narrative"]);
        narrativeText = combined.narrative;
        healer.recordSuccess("narrative-turn");

        // Parse beat JSON inline — no separate LLM call needed
        const lastAction = recentActions[recentActions.length - 1] ?? actionText;
        if (combined.beatJson) {
          parseBeatAndRecord(combined.beatJson, lastAction, currentLocation).catch(() => {});
        }
      } else {
        const err = dagResult.errors["narrative"];
        narrativeText = `[Error: ${String(err?.message ?? "unknown")}]\n\nThe narrator falters. Try again.`;
        healer.recordFailure("narrative-turn", "empty_response");
      }

      // Extract options
      if (dagResult.outputs["options"]) {
        try {
          const parsed = parseLlmJson(dagResult.outputs["options"]);
          if (Array.isArray(parsed) && parsed.length >= 4) {
            choices = parsed.slice(0, 4);
            healer.recordSuccess("option-generation");
          } else {
            healer.recordFailure("option-generation", "structure_mismatch");
          }
        } catch {
          console.warn("[Options] Failed to parse turn options from DAG");
          healer.recordFailure("option-generation", "json_parse");
        }
      }

      // Persist narrative for resume (best-effort)
      saveNarrative(narrativeText).catch(() => {});

      // Save healer state after each turn
      healer.save();
    } catch (err) {
      narrativeText = `[Error: ${String(err)}]\n\nThe engine encountered an error processing your action. Try again.`;
    } finally {
      // Dismiss minigame overlay now that content is ready
      if (showMinigame && minigameRef) {
        minigameRef.dismiss();
      }
      showMinigame = false;
      processing = false;
    }
  }

  /**
   * Parse beat JSON from the combined response and record it. No LLM call needed.
   * Also triggers memory compression and pact detection as side effects.
   */
  async function parseBeatAndRecord(
    beatRaw: string,
    action: string,
    location: string,
  ): Promise<void> {
    try {
      const json = parseBeatJson(beatRaw);
      if (typeof json !== "object" || json === null) {
        healer.recordFailure("narrative-turn", "structure_mismatch");
        return;
      }
      const rec = json as Record<string, unknown>;
      const consequences = (rec.consequences as string[]) ?? [];
      const beat = {
        turn: turnNumber,
        action: (rec.action as string) ?? action,
        outcome: (rec.outcome as string) ?? "",
        consequences,
        mood: (rec.mood as string) ?? "neutral",
        location: location,
        npcs_involved: (rec.npcs_involved as string[]) ?? [],
        arcs_advanced: (rec.arcs_advanced as string[]) ?? [],
        arcs_referenced: (rec.arcs_referenced as string[]) ?? [],
        tags: (rec.tags as string[]) ?? [],
      };
      await recordBeat(JSON.stringify(beat));

      // Trigger memory compression check (best-effort, non-blocking)
      checkMemoryCompression().catch((err) =>
        console.warn("[MemoryCompression] Failed (non-fatal):", err),
      );

      // Scan for pact patterns in consequences (best-effort)
      detectAndRecordPacts(consequences).catch((err) =>
        console.warn("[PactDetection] Failed (non-fatal):", err),
      );
    } catch (err) {
      healer.recordFailure("narrative-turn", "json_parse");
      console.warn("[BeatParsing] Failed (non-fatal):", err);
    }
  }

  /**
   * Check if memory needs compression and trigger LLM-based compression.
   *
   * - If immediate beats > 5: generate a one-line summary for the overflow beat
   *   via beatSummaryPrompt and record it in recent memory via Tauri.
   * - If recent summaries > 20: compress older summaries into key facts
   *   via memoryCompressionPrompt and add them to deep memory via Tauri.
   *
   * Best-effort: failures are silently ignored.
   */
  async function checkMemoryCompression(): Promise<void> {
    if (!narrativeAgent) return;
    const agent = narrativeAgent;

    try {
      const memory = await getNarrativeMemory();

      // If immediate beats overflowed (>5), the engine already created a
      // placeholder summary. Generate an LLM-produced summary to replace it.
      if (memory.immediate_count > 5) {
        const lastAction = recentActions[recentActions.length - 1] ?? "";
        const prompt =
          beatSummaryPrompt(lastAction, narrativeText.slice(0, 300)) +
          healer.healingSuffix("beat-extraction");
        const systemCtx = "Summarize this RPG turn in one sentence. Return ONLY the sentence.";
        try {
          const summary = await agent.generateWithRouter(prompt, "beat-extraction", systemCtx);
          if (summary.trim()) {
            // Record the summary as a beat so the engine can update_summary
            await recordBeat(
              JSON.stringify({
                turn: turnNumber,
                action: lastAction,
                outcome: summary.trim(),
                consequences: [],
                mood: "neutral",
                location: currentLocation,
                npcs_involved: [],
                arcs_advanced: [],
                arcs_referenced: [],
                tags: [],
              }),
            );
            healer.recordSuccess("beat-extraction");
          }
        } catch {
          healer.recordFailure("beat-extraction", "empty_response");
        }
      }

      // If recent summaries > 20, compress into deep memory key facts
      if (memory.recent_count > 20) {
        const summaryLines = memory.recent_text.split("\n").filter((l) => l.trim().length > 0);
        if (summaryLines.length > 0) {
          const prompt =
            memoryCompressionPrompt(summaryLines) + healer.healingSuffix("beat-extraction");
          const systemCtx =
            "Compress RPG turn summaries into key facts. Return ONLY a JSON array of strings.";
          try {
            const raw = await agent.generateWithRouter(prompt, "beat-extraction", systemCtx);
            const facts = parseLlmJson(raw);
            if (Array.isArray(facts) && facts.length > 0) {
              // Record facts as a special beat with the compressed info
              // The engine's record_beat will push to memory tiers
              for (const fact of facts) {
                if (typeof fact === "string" && fact.trim()) {
                  await recordBeat(
                    JSON.stringify({
                      turn: turnNumber,
                      action: "memory-compression",
                      outcome: fact.trim(),
                      consequences: [],
                      mood: "neutral",
                      location: currentLocation,
                      npcs_involved: [],
                      arcs_advanced: [],
                      arcs_referenced: [],
                      tags: [],
                    }),
                  );
                }
              }
              healer.recordSuccess("beat-extraction");
            }
          } catch {
            healer.recordFailure("beat-extraction", "json_parse");
          }
        }
      }
    } catch {
      // getNarrativeMemory failed — non-fatal
    }
  }

  /**
   * Scan beat consequences for pact patterns and record pacts.
   *
   * Detects patterns like "owes_favor:X", "promised:X", "bound_to:X", "debt:X"
   * in the consequences array. For each match, attempts to invoke a Tauri
   * command to create the pact.
   *
   */
  async function detectAndRecordPacts(consequences: string[]): Promise<void> {
    for (const consequence of consequences) {
      for (const pattern of PACT_PATTERNS) {
        if (consequence.startsWith(pattern)) {
          const target = consequence.substring(pattern.length).trim();
          if (!target) continue;
          await createPact({
            pattern: pattern.replace(":", ""),
            target,
            turn: turnNumber,
          });
          console.info(
            `[PactDetection] Recorded pact: ${pattern.replace(":", "")} with "${target}" (turn ${turnNumber})`,
          );
        }
      }
    }
  }

  /** Build fallback narrative from engine data (no LLM). */
  function buildFallbackNarrative(result: TurnResult): string {
    const ctx = result.narrative_context;
    const parts: string[] = [];
    if (ctx.skill_results.length > 0) {
      parts.push(`[${ctx.skill_results.join(" | ")}]`);
      parts.push("");
    }
    parts.push(`> ${ctx.action}`);
    parts.push("");
    parts.push(`${ctx.time_description} — ${ctx.location}`);
    parts.push("");
    for (const event of ctx.events) parts.push(`* ${event}`);
    if (ctx.events.length > 0) parts.push("");
    for (const note of ctx.social_notes) parts.push(`! ${note}`);
    for (const effect of result.social_effects) parts.push(`! ${effect}`);
    return parts.join("\n");
  }

  // Restore state from resume data if provided
  let resumeApplied = false;
  $effect(() => {
    if (resumeData && !resumeApplied) {
      resumeApplied = true;
      characterName = resumeData.characterName;
      characterBackground = resumeData.characterBackground;
      turnNumber = resumeData.turnNumber;
      currentLocation = resumeData.currentLocation;
      recentActions = resumeData.recentActions;
      narrativeText = resumeData.lastNarrative || "";
      typewriterDone = true; // Don't re-animate old text on resume
      statusLine = `Turn ${resumeData.turnNumber} | Resumed`;
      // Generate options for the resumed state
      ensureAgents();
      generateOptions().catch((err) => console.warn("[Options] Resume options failed:", err));
    }
  });

  // Start opening scene if needed
  $effect(() => {
    if (isOpeningScene && turnNumber === 0 && !processing && !narrativeText) {
      handleOpeningScene();
    }
  });

  function handleKeydown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    const key = e.key.toLowerCase();

    const overlayKind = OVERLAY_KEYS[key];
    if (overlayKind) {
      if (overlayKind !== "settings" && !typewriterDone) return;
      e.preventDefault();
      overlayStore.toggle(overlayKind);
      return;
    }

    if (key === "q" && overlayStore.active === null) {
      e.preventDefault();
      onQuit();
    }

    if (e.key === "Escape" && overlayStore.active !== null) {
      e.preventDefault();
      overlayStore.close();
    }
  }

  /** Handle hotkey bar tap from mobile touch. */
  function handleHotkeyTap(key: string): void {
    const overlayKind = OVERLAY_KEYS[key];
    if (overlayKind) {
      if (overlayKind !== "settings" && !typewriterDone) return;
      overlayStore.toggle(overlayKind);
      return;
    }
    if (key === "q" && overlayStore.active === null) {
      onQuit();
    }
  }

  function handleChoice(index: number): void {
    if (!typewriterDone || processing) return;
    const choice = choices[index];
    if (choice) {
      submitAction(choice.text);
    }
  }

  function handleTypewriterComplete(): void {
    typewriterDone = true;
  }

  function handleFreeformSubmit(): void {
    if (freeformInput.trim() && !processing) {
      const action = freeformInput.trim();
      freeformInput = "";
      submitAction(action);
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="game-screen">
  <AsciiAnimation
    animationLevel={settingsStore.animationLevel}
    glyphPercent={settingsStore.glyphDensity}
    glyphSpeedMs={settingsStore.glyphSpeedMs}
    narrativeContext={narrativeText}
    characterArchetype={characterBackground}
    turnCounter={turnNumber}
  />

  {#if lastSkillSummary}
    <div class="game-screen__skill-bar">{lastSkillSummary}</div>
  {/if}

  <div class="game-screen__narrative" style="background: {narrativeBg};">
    {#if processing && !narrativeText}
      <div class="game-screen__processing-scene">{processingText}</div>
    {:else if narrativeText}
      <Typewriter
        text={narrativeText}
        wpm={settingsStore.wpm}
        onComplete={handleTypewriterComplete}
      />
    {/if}
  </div>

  {#if statusLine}
    <div class="game-screen__status">{statusLine}</div>
  {/if}

  <div class="game-screen__input">
    {#if processing}
      <div class="game-screen__processing">{processingText}</div>
    {:else if isGuided}
      {#if typewriterDone && choices.length > 0}
        {#key choices}
          <ChoiceGrid options={choices} onSelect={handleChoice} />
        {/key}
      {/if}
    {:else}
      <div class="game-screen__freeform" class:game-screen__freeform--disabled={!typewriterDone}>
        <span class="game-screen__prompt">&gt;</span>
        <input
          class="game-screen__freeform-input"
          type="text"
          maxlength={256}
          placeholder={typewriterDone ? "What do you do? (256 chars max)" : "..."}
          disabled={!typewriterDone}
          bind:value={freeformInput}
          onkeydown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              handleFreeformSubmit();
            }
          }}
        />
        <span class="game-screen__charcount">{freeformInput.length}/256</span>
        <button
          class="game-screen__submit-btn"
          disabled={!typewriterDone || !freeformInput.trim()}
          onclick={handleFreeformSubmit}>Submit</button
        >
      </div>
    {/if}
  </div>

  <HotkeyBar keys={hotkeyDefs} onOverlay={handleHotkeyTap} />

  <OverlayContainer {activeOverlay} onClose={() => overlayStore.close()} />

  {#if showMinigame}
    <MinigameOverlay bind:this={minigameRef} onDismiss={handleMinigameDismiss} />
  {/if}
</div>

<style>
  .game-screen {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .game-screen__skill-bar {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    padding: 0.2rem 0.75rem;
    background: rgba(10, 10, 10, 0.85);
    color: var(--fg-dim);
    font-size: 0.7rem;
    border-bottom: 1px solid var(--fg-dim);
    font-family: var(--font-mono);
  }

  .game-screen__narrative {
    position: relative;
    z-index: 1;
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* background set inline via narrativeBg derived value */
  }

  .game-screen__processing-scene {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 0.5rem 0.75rem;
    color: var(--fg-dim);
    font-family: var(--font-mono);
    font-style: italic;
    font-size: 1rem;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }

  .game-screen__status {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    padding: 0.15rem 0.75rem;
    color: var(--fg-dim);
    font-size: 0.7rem;
    font-family: var(--font-mono);
    border-top: 1px solid var(--fg-dim);
    background: rgba(10, 10, 10, 0.85);
  }

  .game-screen__input {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    max-height: 35vh;
    overflow-y: auto;
    background: rgba(10, 10, 10, 0.85);
  }

  .game-screen__processing {
    padding: 0.4rem 0.75rem;
    color: var(--fg-dim);
    font-style: italic;
    font-family: var(--font-mono);
    font-size: 0.85rem;
  }

  .game-screen__freeform {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.75rem;
    border-top: 1px solid var(--fg-dim);
  }

  .game-screen__freeform--disabled {
    opacity: 0.35;
  }

  .game-screen__prompt {
    color: var(--fg-bright);
    font-weight: bold;
    font-size: 1rem;
  }

  .game-screen__freeform-input {
    flex: 1;
    background: transparent;
    border: none;
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.85rem;
    outline: none;
    caret-color: var(--fg-bright);
  }

  .game-screen__freeform-input::placeholder {
    color: var(--fg-dim);
  }

  .game-screen__freeform-input:disabled {
    color: var(--fg-dim);
  }

  .game-screen__charcount {
    color: var(--fg-dim);
    font-size: 0.7rem;
    white-space: nowrap;
  }

  .game-screen__submit-btn {
    background: transparent;
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    padding: 0.25rem 0.6rem;
    min-height: 44px;
    cursor: pointer;
    white-space: nowrap;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .game-screen__submit-btn:active:not(:disabled) {
    background: rgba(51, 255, 51, 0.15);
    color: var(--fg-bright);
  }

  .game-screen__submit-btn:disabled {
    color: var(--fg-dim);
    border-color: transparent;
    cursor: default;
  }

  @media (max-width: 600px) {
    .game-screen__skill-bar {
      font-size: 0.65rem;
      padding: 0.15rem 0.5rem;
    }

    .game-screen__status {
      font-size: 0.65rem;
      padding: 0.1rem 0.5rem;
    }

    .game-screen__freeform {
      padding: 0.3rem 0.5rem;
      gap: 0.3rem;
    }

    .game-screen__freeform-input {
      font-size: 0.8rem;
    }

    .game-screen__submit-btn {
      font-size: 0.7rem;
      padding: 0.2rem 0.5rem;
      min-height: 36px;
    }
  }
</style>
